<script setup lang="ts">
    // @ts-nocheck
    // nocheck pga att vi har urgamla versioner av allt och TS kaosar

    import KButton from "@/components/Button.vue";
    import { useStore } from "@/store";
    import { CONFIG_PROVIDE_NAME, ConfigType } from "@/store/modules/types/configModuleTypes";
    import { CollapseTransition } from "@ivanv/vue-collapse-transition";
    import { computed, inject, provide, ref, toRefs } from "vue";

    const store = useStore();

    interface Props {
        /** Namn på sektionen. Används för hämta inställningar */
        name: string;
        defaultExpanded?: boolean;
        visibleContent?: string;
        hiddenContent?: string;
        /**
         * Namn som läggs på i slutet av CONFIG_PROVIDE_NAME. Används för att kunna sätta fler provide i samma komponent
         * och att kunna hämta en specifik samling inställningar.
         * SKA GENERELLT INTE ANVÄNDAS (med undantag de platser som använder FormulateInput och har flera sektioner,
         * t.ex Opjournalen). Använd K-formulärkomponenterna istället i nya komponenter.
         *
         * KAN TAS BORT NÄr VI INTE LÄNGER HAR <FormulateInput>-komponenter, utan helt gått över till
         * K-formulärkomponenter eller de äldre InputWrapper
         */
        customConfigProvideSuffix?: string;
    }

    const props = withDefaults(defineProps<Props>(), {
        defaultExpanded: false,
        visibleContent: "Dölj alternativ",
        hiddenContent: "Visa alternativ",
    });

    const { name, customConfigProvideSuffix, defaultExpanded } = toRefs(props);

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    }>(CONFIG_PROVIDE_NAME);

    /**
     * Overridar CONFIG_PROVIDE_NAME provide med inställningar baserat på custom namn. Alla child av KSection
     * kommer istället få det moduleName och subModuleName som är satt här.
     *
     * KAN TAS BORT NÄr VI INTE LÄNGER HAR <FormulateInput>-komponenter, utan helt gått över till K-formulärkomponenter
     * eller de äldre InputWrapper
     */
    if (customConfigProvideSuffix?.value) {
        const customConfigNames = inject<{
            moduleName: string;
            subModuleName: string;
        }>(`${CONFIG_PROVIDE_NAME}-${customConfigProvideSuffix.value}`);

        provide(CONFIG_PROVIDE_NAME, {
            moduleName: configNames?.moduleName,
            subModuleName: customConfigNames?.subModuleName,
        });
    }

    const show = ref(defaultExpanded.value);

    const toggle = () => {
        show.value = !show.value;
    };

    const shouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            name: name.value,
            moduleName: configNames?.moduleName,
            subModuleName: configNames?.subModuleName,
            type: ConfigType.Group,
        })
    );

    defineExpose({
        toggle,
    });
</script>

<template>
    <div v-if="shouldBeVisible" class="k-inner-section span-all">
        <KButton
            variant="hollow"
            size="adaptable"
            type="button"
            :class="['toggle', 'span-all', { show }]"
            @click="toggle"
        >
            <div :class="['chevron', { rotate: show }]">
                <img src="@/assets/dropdown-arrow-white.svg" alt="" />
            </div>
            <span class="toggle-content">{{ show ? visibleContent : hiddenContent }}</span>
        </KButton>
        <collapse-transition>
            <div v-show="show" class="content span-all">
                <slot v-show="show" />
            </div>
        </collapse-transition>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/form";

    .k-inner-section {
        display: grid;
        grid-template-columns: subgrid;
        grid-auto-flow: row;

        .content {
            display: grid;
            grid-template-columns: subgrid;
        }

        .toggle {
            padding-left: 0;
            gap: 0.4rem;
            transition: 0.4s all;

            &.show {
                margin-bottom: 0.8rem;
            }

            .chevron {
                display: grid;
                place-items: center;
                background-color: $primary;
                border-radius: 50%;
                width: 1.2rem;
                height: 1.2rem;
                transition: 0.2s all;

                &.rotate {
                    transform: rotate(180deg);
                }

                img {
                    height: 50%;
                    width: auto;
                }
            }

            .toggle-content {
                display: flex;
                align-self: flex-end;
            }
        }
    }
</style>
